<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  emits: ['updated'],
  data() {
    return {
      structure: null,
      components: [],
      allComponents: [],
      loading: false,

      addForm: { salary_component_id: '', override_value: '' },
      addErrors: {},

      bulkMode: false,
      bulkSelected: [],

      editingId: null,
      editValue: '',

      calcLabels: {
        fixed: 'Fixed',
        percentage_of_gross: '% Gross',
        percentage_of_basic: '% Basic',
        percentage_of_ctc: '% CTC',
      },
    };
  },
  computed: {
    attachedIds() {
      return this.components.map((c) => c.component_id);
    },
    availableComponents() {
      return this.allComponents.filter((c) => !this.attachedIds.includes(c.id));
    },
    earnings() {
      return this.components.filter((c) => c.type === 'earning');
    },
    deductions() {
      return this.components.filter((c) => c.type === 'deduction');
    },
  },
  methods: {
    open(structure) {
      this.structure = structure;
      this.resetForms();
      this.fetchAll();
    },

    async fetchAll() {
      await Promise.all([this.fetchComponents(), this.fetchAllComponents()]);
    },

    async fetchComponents() {
      if (!this.structure) return;
      this.loading = true;
      try {
        const res = await axios.get(`/hrms/salary-structures/${this.structure.id}/components`);
        this.components = res.data.data || [];
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    async fetchAllComponents() {
      try {
        const res = await axios.get('/hrms/salary-components/dropdown');
        this.allComponents = res.data.data || [];
      } catch (error) {
        console.log(error.message);
      }
    },

    componentLabel(c) {
      return c.code ? `${c.name} (${c.code})` : c.name;
    },
    calcLabel(v) {
      return this.calcLabels[v] || v || '-';
    },

    effectiveValue(row) {
      const num = parseFloat(row.effective_value);
      if (isNaN(num)) return '-';
      if (row.calculation_type === 'fixed') return num.toLocaleString();
      return `${num}%`;
    },

    // ---------- SINGLE ADD ----------
    async addComponent() {
      this.addErrors = {};
      if (!this.addForm.salary_component_id) {
        this.addErrors = { salary_component_id: ['Please select a component.'] };
        return;
      }
      this.setLoading(true);
      try {
        const res = await axios.post(`/hrms/salary-structures/${this.structure.id}/components`, {
          salary_component_id: this.addForm.salary_component_id,
          override_value: this.addForm.override_value === '' ? null : this.addForm.override_value,
        });
        this.$swalToast(res.data.message);
        this.addForm = { salary_component_id: '', override_value: '' };
        await this.fetchComponents();
        this.$emit('updated');
      } catch (error) {
        if (error.type === 'validation') this.addErrors = error.errors;
        else this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    // ---------- BULK ADD ----------
    toggleBulk(id) {
      const i = this.bulkSelected.indexOf(id);
      if (i === -1) this.bulkSelected.push(id);
      else this.bulkSelected.splice(i, 1);
    },

    async bulkAdd() {
      if (!this.bulkSelected.length) {
        this.$swalToast('Please select at least one component.', 'error');
        return;
      }
      this.setLoading(true);
      try {
        const payload = {
          components: this.bulkSelected.map((id) => ({ salary_component_id: id, override_value: null })),
        };
        const res = await axios.post(`/hrms/salary-structures/${this.structure.id}/components/bulk`, payload);
        this.$swalToast(res.data.message);
        this.bulkSelected = [];
        this.bulkMode = false;
        await this.fetchComponents();
        this.$emit('updated');
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    // ---------- EDIT OVERRIDE ----------
    startEdit(row) {
      this.editingId = row.id;
      this.editValue = row.override_value ?? '';
    },
    cancelEdit() {
      this.editingId = null;
      this.editValue = '';
    },
    async saveEdit(row) {
      this.setLoading(true);
      try {
        const res = await axios.put(`/hrms/salary-structures/${this.structure.id}/components/${row.id}`, {
          override_value: this.editValue === '' ? null : this.editValue,
        });
        this.$swalToast(res.data.message);
        this.cancelEdit();
        await this.fetchComponents();
        this.$emit('updated');
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    // ---------- REMOVE ----------
    async removeComponent(row) {
      this.setLoading(true);
      try {
        const res = await axios.delete(`/hrms/salary-structures/${this.structure.id}/components/${row.id}`);
        this.$swalToast(res.data.message);
        await this.fetchComponents();
        this.$emit('updated');
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    resetForms() {
      this.components = [];
      this.addForm = { salary_component_id: '', override_value: '' };
      this.addErrors = {};
      this.bulkMode = false;
      this.bulkSelected = [];
      this.editingId = null;
      this.editValue = '';
    },
  },
};
</script>

<template>
  <div class="modal fade" id="hrm_structure_components_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content sc-modal">

        <!-- Header -->
        <div class="modal-header sc-header">
          <div>
            <h4 class="modal-title mb-0">Manage Components</h4>
            <span v-if="structure" class="sc-subtitle">{{ structure.name }}</span>
          </div>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body sc-body">

          <!-- Add bar -->
          <div class="sc-add-card mb-4">
            <template v-if="!bulkMode">
              <div class="row g-2 align-items-end">
                <div class="col-md-5">
                  <label class="sc-label">Component</label>
                  <select class="form-select" v-model="addForm.salary_component_id">
                    <option value="">Select component</option>
                    <option v-for="c in availableComponents" :key="c.id" :value="c.id">{{ componentLabel(c) }}</option>
                  </select>
                  <div v-if="addErrors.salary_component_id" class="text-danger small mt-1">{{ addErrors.salary_component_id[0] }}</div>
                </div>
                <div class="col-md-4">
                  <label class="sc-label">Override Value <span class="sc-optional">(optional)</span></label>
                  <input type="number" step="0.01" class="form-control" v-model="addForm.override_value" placeholder="Default" />
                </div>
                <div class="col-md-3 d-flex gap-2">
                  <button type="button" class="btn btn-primary flex-fill" @click="addComponent">
                    <i class="ti ti-plus me-1"></i>Add
                  </button>
                  <button type="button" class="btn btn-outline-secondary" @click="bulkMode = true" title="Bulk add">
                    <i class="ti ti-list-check"></i>
                  </button>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="d-flex align-items-center justify-content-between mb-2">
                <label class="sc-label mb-0">Select components to add</label>
                <button type="button" class="btn btn-sm btn-link text-decoration-none p-0" @click="bulkMode = false; bulkSelected = []">
                  <i class="ti ti-x me-1"></i>Cancel
                </button>
              </div>
              <div v-if="availableComponents.length" class="d-flex flex-wrap gap-2">
                <button
                  v-for="c in availableComponents"
                  :key="c.id"
                  type="button"
                  class="bulk-pill"
                  :class="{ active: bulkSelected.includes(c.id) }"
                  @click="toggleBulk(c.id)"
                >
                  <i v-if="bulkSelected.includes(c.id)" class="ti ti-check me-1"></i>{{ componentLabel(c) }}
                </button>
              </div>
              <p v-else class="text-muted small mb-0">All components already added.</p>
              <button type="button" class="btn btn-primary btn-sm mt-3" @click="bulkAdd" :disabled="!bulkSelected.length">
                <i class="ti ti-plus me-1"></i>Add {{ bulkSelected.length }} selected
              </button>
            </template>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="sc-empty">
            <div class="spinner-border spinner-border-sm text-primary mb-2"></div>
            <div>Loading components...</div>
          </div>

          <!-- Empty -->
          <div v-else-if="!components.length" class="sc-empty">
            <i class="ti ti-cash-off d-block mb-2" style="font-size:32px; opacity:.4;"></i>
            No components added to this structure yet.
            <div class="small text-muted mt-1">Add components using the form above.</div>
          </div>

          <template v-else>
            <!-- EARNINGS -->
            <div v-if="earnings.length" class="sc-group">
              <div class="sc-group-head sc-head-earning">
                <span><i class="ti ti-trending-up me-1"></i>Earnings</span>
                <span class="sc-count">{{ earnings.length }}</span>
              </div>
              <div class="sc-rows">
                <div v-for="row in earnings" :key="row.id" class="sc-row">
                  <div class="sc-col-name">
                    <span class="sc-name">{{ row.name }}</span>
                    <span class="sc-code">{{ row.code }}</span>
                  </div>
                  <div class="sc-col-calc">{{ calcLabel(row.calculation_type) }}</div>
                  <div class="sc-col-value">{{ effectiveValue(row) }}</div>
                  <div class="sc-col-override">
                    <template v-if="editingId === row.id">
                      <div class="d-flex gap-1 align-items-center">
                        <input type="number" step="0.01" class="form-control form-control-sm sc-edit-input" v-model="editValue" placeholder="default" />
                        <button class="btn btn-sm btn-success sc-icon-btn" @click="saveEdit(row)"><i class="ti ti-check"></i></button>
                        <button class="btn btn-sm btn-light sc-icon-btn" @click="cancelEdit"><i class="ti ti-x"></i></button>
                      </div>
                    </template>
                    <template v-else>
                      <span v-if="row.override_value !== null && row.override_value !== ''" class="sc-override-val">{{ row.override_value }}</span>
                      <span v-else class="sc-default">default</span>
                      <button class="btn btn-sm sc-icon-btn sc-edit-trigger" @click="startEdit(row)" title="Edit override">
                        <i class="ti ti-edit"></i>
                      </button>
                    </template>
                  </div>
                  <div class="sc-col-action">
                    <button v-if="!row.is_statutory" class="btn btn-sm sc-icon-btn text-danger" @click="removeComponent(row)" title="Remove">
                      <i class="ti ti-trash"></i>
                    </button>
                    <span v-else class="sc-required" title="Statutory components cannot be removed">Required</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- DEDUCTIONS -->
            <div v-if="deductions.length" class="sc-group">
              <div class="sc-group-head sc-head-deduction">
                <span><i class="ti ti-trending-down me-1"></i>Deductions</span>
                <span class="sc-count">{{ deductions.length }}</span>
              </div>
              <div class="sc-rows">
                <div v-for="row in deductions" :key="row.id" class="sc-row">
                  <div class="sc-col-name">
                    <span class="sc-name">{{ row.name }}</span>
                    <span class="sc-code">{{ row.code }}</span>
                  </div>
                  <div class="sc-col-calc">{{ calcLabel(row.calculation_type) }}</div>
                  <div class="sc-col-value">{{ effectiveValue(row) }}</div>
                  <div class="sc-col-override">
                    <template v-if="editingId === row.id">
                      <div class="d-flex gap-1 align-items-center">
                        <input type="number" step="0.01" class="form-control form-control-sm sc-edit-input" v-model="editValue" placeholder="default" />
                        <button class="btn btn-sm btn-success sc-icon-btn" @click="saveEdit(row)"><i class="ti ti-check"></i></button>
                        <button class="btn btn-sm btn-light sc-icon-btn" @click="cancelEdit"><i class="ti ti-x"></i></button>
                      </div>
                    </template>
                    <template v-else>
                      <span v-if="row.override_value !== null && row.override_value !== ''" class="sc-override-val">{{ row.override_value }}</span>
                      <span v-else class="sc-default">default</span>
                      <button class="btn btn-sm sc-icon-btn sc-edit-trigger" @click="startEdit(row)" title="Edit override">
                        <i class="ti ti-edit"></i>
                      </button>
                    </template>
                  </div>
                  <div class="sc-col-action">
                    <button v-if="!row.is_statutory" class="btn btn-sm sc-icon-btn text-danger" @click="removeComponent(row)" title="Remove">
                      <i class="ti ti-trash"></i>
                    </button>
                    <span v-else class="sc-required" title="Statutory components cannot be removed">Required</span>
                  </div>
                </div>
              </div>
            </div>

            <p class="sc-note">
              <i class="ti ti-info-circle me-1"></i>
              "Effective Value" shows the override if set, otherwise the component's default. Percentage components are calculated on the employee's salary.
            </p>
          </template>

        </div>

        <div class="modal-footer sc-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sc-modal { border: none; border-radius: 16px; overflow: hidden; }

.sc-header {
  background: #fff;
  border-bottom: 1px solid #eef2f7;
  padding: 18px 22px;
}
.sc-subtitle {
  display: inline-block;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #6366f1;
  background: #eef2ff;
  padding: 2px 10px;
  border-radius: 20px;
}

.sc-body { padding: 22px; background: #f8fafc; max-height: 70vh; overflow-y: auto; }

/* Add card */
.sc-add-card {
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(15,23,42,.04);
}
.sc-label { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 5px; display: block; }
.sc-optional { color: #94a3b8; font-weight: 400; }

/* Bulk pills */
.bulk-pill {
  border: 1.5px solid #e2e8f0; background: #fff; color: #475569;
  font-size: 12px; font-weight: 600; padding: 5px 12px;
  border-radius: 8px; cursor: pointer; transition: all .15s ease;
}
.bulk-pill:hover { border-color: #c7d2fe; }
.bulk-pill.active { background: #6366f1; border-color: #6366f1; color: #fff; }

/* Group cards */
.sc-group {
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(15,23,42,.04);
}
.sc-group-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px;
  font-size: 12.5px; font-weight: 700;
}
.sc-head-earning { background: #f0fdf4; color: #16a34a; border-bottom: 1px solid #dcfce7; }
.sc-head-deduction { background: #fef2f2; color: #dc2626; border-bottom: 1px solid #fee2e2; }
.sc-count {
  font-size: 11px; font-weight: 700;
  background: rgba(255,255,255,.7); padding: 1px 9px; border-radius: 20px;
}

/* Rows */
.sc-row {
  display: grid;
  grid-template-columns: 1fr 90px 110px 190px 70px;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border-bottom: 1px solid #f1f5f9;
  transition: background .12s ease;
}
.sc-row:last-child { border-bottom: none; }
.sc-row:hover { background: #fafbfc; }

.sc-col-name { display: flex; align-items: center; gap: 7px; min-width: 0; }
.sc-name { font-size: 13px; font-weight: 600; color: #1e293b; }
.sc-code {
  font-size: 9.5px; font-weight: 700; letter-spacing: .4px;
  padding: 1px 6px; border-radius: 4px;
  background: #f1f5f9; color: #64748b; text-transform: uppercase;
}
.sc-col-calc { font-size: 11.5px; color: #64748b; }
.sc-col-value { font-size: 13px; font-weight: 700; color: #0f172a; }

.sc-col-override { display: flex; align-items: center; gap: 4px; }
.sc-override-val { font-size: 12.5px; font-weight: 600; color: #1e293b; }
.sc-default { font-size: 11px; color: #cbd5e1; font-style: italic; }
.sc-edit-input { width: 90px; }
.sc-edit-trigger { color: #94a3b8; }
.sc-edit-trigger:hover { color: #6366f1; }

.sc-icon-btn { padding: 3px 7px; line-height: 1; }
.sc-icon-btn i { font-size: 14px; }

.sc-required {
  font-size: 9.5px; font-weight: 700;
  padding: 3px 9px; border-radius: 5px;
  background: #fef3c7; color: #b45309; cursor: help;
}

/* Empty / loading */
.sc-empty {
  text-align: center; padding: 40px 20px;
  color: #94a3b8; font-size: 13px;
}

.sc-note {
  font-size: 11px; color: #94a3b8;
  background: #fff; border: 1px dashed #e2e8f0;
  border-radius: 8px; padding: 10px 12px; margin: 0;
  line-height: 1.5;
}

.sc-footer { background: #fff; border-top: 1px solid #eef2f7; padding: 14px 22px; }

@media (max-width: 640px) {
  .sc-row { grid-template-columns: 1fr auto; row-gap: 6px; }
  .sc-col-calc, .sc-col-value { grid-column: 1 / -1; font-size: 11px; }
}
</style>